import type { NextRequest, NextResponse } from 'next/server'

import { configPublic } from '@src/config.public'
import { getLocaleFromCode } from '@src/i18n/locale.util'
import type { CountryCode } from '@src/i18n/locales'
import { log } from '@src/utils/log.util'

import { HEADERS } from './constants.util'

/**
 * Get the current URL from the 'url' custom request header (server-side) or window.location (client-side).
 * Needed as NextRequest.nextUrl and NextRequest.url doesn't work with reverse proxy (CloudFront).
 **/
export async function getCurrentURL(req?: NextRequest | NextResponse) {
  try {
    // for middleware
    if (req) {
      return new URL(String(req.headers.get(HEADERS.url)))
    } // for server-side
    else if (typeof window === 'undefined') {
      const { headers } = await import('next/headers')
      return new URL(String((await headers()).get(HEADERS.url)))
    } // for client-side
    else {
      return new URL(window.location.href)
    }
  } catch (error) {
    log.error('getCurrentURL failed')
    return new URL('http://getCurrentURL.error')
  }
}

/**
 * Same as getCurrentURL but overrides the hostname using the 'host' header in the HTTP request.
 * Needed because NextRequest.nextUrl and NextRequest.url doesn't translate '127.0.0.1' to 'localhost'.
 * @param req NextRequest or NextResponse for middleware usage. (Optional)
 * @returns URL
 */
export async function getCurrentURLFromHeaders(req?: NextRequest | NextResponse) {
  const currentUrl = await getCurrentURL(req)
  const currentHost = await getCurrentHost(req)
  return new URL(`${currentUrl.protocol}//${currentHost}${currentUrl.pathname}${currentUrl.search}`)
}

/**
 * Get the current host from the 'host' custom request header (server-side) or window.location.host (client-side).
 * Needed as NextRequest.nextUrl and NextRequest.url doesn't transtale '127.0.0.1' to 'localhost'.
 * @param req NextRequest or NextResponse for middleware usage. (Optional)
 * @returns string
 */
export async function getCurrentHost(req?: NextRequest | NextResponse) {
  const currentUrl = await getCurrentURL(req)
  // for middleware
  if (req) {
    return String(req.headers.get(HEADERS.host) ?? currentUrl.host)
  } // for server-side
  else if (typeof window === 'undefined') {
    const { headers } = await import('next/headers')
    return String((await headers()).get(HEADERS.host) ?? currentUrl.host)
  } // for client-side
  else {
    return window.location.host
  }
}

/**
 * Strips the origin from a URL href and returns a relative href (pathname + search + hash).
 */
export function getRelativeHref(url: URL) {
  return url.href.replace(url.origin, '')
}

/**
 * Isomorphic version of Next.js useSearchParams hook and NextRequest.nextUrl.searchParams.
 * @see https://nextjs.org/docs/app/api-reference/functions/use-search-params
 */
export async function isomorphicSearchParams(args?: { request?: NextRequest }) {
  const currentUrl = await getCurrentURL(args?.request)
  return new URLSearchParams(currentUrl.search)
}

// Shopify URL utilities

export interface BuildShopifyUrlOptions {
  productPath?: string
  includeEnglishLangSegment?: boolean
}

/**
 * Build a Shopify store URL for a given commerce-web locale.
 * Examples:
 *  - en-US → https://shop.bodi.com/products/shakeology
 *  - en-CA → https://shop-ca.bodi.com/products/shakeology
 *  - fr-CA → https://shop-ca.bodi.com/fr/products/shakeology
 *  - en-GB → https://shop-uk.bodi.com/products/shakeology
 */
export function buildShopifyProductUrl(
  localeCode: string,
  options: BuildShopifyUrlOptions = {}
): string {
  const { productPath = '', includeEnglishLangSegment = false } = options

  const locale = getLocaleFromCode(localeCode)
  const country = locale.countryCode as CountryCode
  const baseUrl = configPublic.shopify.baseUrlMap?.[country]
  if (!baseUrl) return ''

  // Get the language path segment from the locale's shopifyLanguagePathSegment property
  // For Shopify URLs, we typically omit the /en segment for English locales
  // Only include it for non-English languages (like /fr for fr-CA) or when explicitly requested
  const shopifyLangSegment = locale.shopifyLanguagePathSegment ?? ''
  const isEnglishSegment = shopifyLangSegment === '/en'

  // Use the language segment if it's not English, or use English segment if explicitly requested
  let langSegment = ''
  if (!isEnglishSegment) {
    langSegment = shopifyLangSegment
  } else if (includeEnglishLangSegment) {
    langSegment = '/en'
  }

  const cleanedPath = productPath.startsWith('/') ? productPath : `/${productPath}`
  return `${baseUrl}${langSegment}${cleanedPath}`
}

export function getShopifyBaseUrl(localeCode: string): string {
  const locale = getLocaleFromCode(localeCode)
  const country = locale.countryCode as CountryCode
  return configPublic.shopify.baseUrlMap?.[country] ?? ''
}

/**
 * Normalize a URL by moving query parameters from the hash to the main URL.
 * This is necessary due to Social Ladder appending query params after the hash
 * @param urlString The URL string to normalize.
 * @returns An object containing the normalized URL and a flag indicating if a redirect is needed.
 */
export function normalizeURL(urlString: string): { url: URL; urlChanged: boolean } {
  if (!URL.canParse(urlString)) {
    throw new Error('Invalid URL')
  }

  const url = new URL(urlString)

  // Check if there are query parameters after the hash
  if (url.hash.includes('?')) {
    const [hash, query] = url.hash.split('?')
    if (!query) {
      url.hash = `${hash}?`
    } else {
      url.search = url.search ? `${url.search}&${query}` : `?${query}`
      url.hash = hash
    }
  }

  return { url, urlChanged: url.toString() !== urlString }
}
